<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="products"
        :row-key="'id'"
        :loading="loading"
        :pagination="pagination"
        :row-selection="rowSelection"
        :scroll="{ x: 1160 }"
        class="text-nowrap align-middle product-report-table"
        @change="handleChange"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_product_reports_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'type'">
            <span class="type-badge" :class="Number(record.Type) === 1 ? 'type-standard' : 'type-service'">
              {{ Number(record.Type) === 1 ? $t(`${i18nScope}.type_standard`) : $t(`${i18nScope}.type_service`) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'price'">
            ${{ formatAmount(record.Price) }}
          </template>

          <template v-else-if="column && column.key === 'quantity'">
            <span class="qty-chip">{{ Number(record.Qty ?? 0) }}</span>
          </template>

          <template v-else-if="column && column.key === 'ordered'">
            {{ Number(record.Total_Ordered ?? 0) }}
          </template>

          <template v-else-if="column && column.key === 'revenue'">
            ${{ formatAmount(record.Revenue) }}
          </template>

          <template v-else-if="column && column.key === 'action'">
            <div class="action-buttons">
              <router-link :to="`/product-details?id=${record.id}`" :title="$t(`${i18nScope}.view`)">
                <i class="ri-eye-line"></i>
              </router-link>
              <router-link :to="`/edit-product?id=${record.id}`" :title="$t(`${i18nScope}.edit`)">
                <i class="ri-edit-line"></i>
              </router-link>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { useI18n } from "vue-i18n";
import type { TablePaginationConfig } from "ant-design-vue";
import type { ProductReportItem } from "@/services/report";

export default defineComponent({
  name: "ProductReport",
  props: {
    products: {
      type: Array as () => ProductReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object as () => TablePaginationConfig,
      required: true,
    },
    selectedProductIds: {
      type: Array as () => number[],
      default: () => [],
    },
    i18nScope: {
      type: String,
      default: "product_report_page",
    },
  },
  emits: ["table-change", "selection-change"],
  setup(props, { emit }) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_code`), key: "sku", dataIndex: "SKU", width: 150 },
      { title: t(`${props.i18nScope}.table_product`), key: "name", dataIndex: "Product_Name", width: 240 },
      { title: t(`${props.i18nScope}.table_category`), key: "category", dataIndex: "Category", width: 180 },
      { title: t(`${props.i18nScope}.table_brand`), key: "brand", dataIndex: "Brand", width: 180 },
      { title: t(`${props.i18nScope}.table_type`), key: "type", dataIndex: "Type", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_in_stock`), key: "quantity", dataIndex: "Qty", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_price`), key: "price", dataIndex: "Price", width: 120, align: "center" as const },
      { title: t(`${props.i18nScope}.table_total_sold`), key: "ordered", dataIndex: "Total_Ordered", width: 130, align: "center" as const },
      { title: t(`${props.i18nScope}.table_revenue`), key: "revenue", dataIndex: "Revenue", width: 140, align: "center" as const },
      { title: t(`${props.i18nScope}.table_action`), key: "action", width: 100, align: "center" as const },
    ]);

    const rowSelection = computed(() => ({
      selectedRowKeys: props.selectedProductIds,
      onChange: (selectedRowKeys: (string | number)[]) => {
        emit("selection-change", selectedRowKeys.map((id) => Number(id)));
      },
    }));

    const formatAmount = (value?: string | number) =>
      Number(value ?? 0).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const handleChange = (pagination: TablePaginationConfig) => {
      emit(
        "table-change",
        pagination.current || 1,
        pagination.pageSize === 1000000 ? "all" : pagination.pageSize || 10
      );
    };

    return {
      columns,
      rowSelection,
      formatAmount,
      handleChange,
    };
  },
});
</script>

<style scoped>
.product-report-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.product-report-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.type-badge,
.qty-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.type-standard {
  color: #1f9254;
  background: #e8f7ee;
}

.type-service {
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.12);
}

.qty-chip {
  color: #1f2940;
  background: #eef2f7;
}

.action-buttons {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.action-buttons a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #5d5fef;
  background: rgba(93, 95, 239, 0.08);
}
</style>
